import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeWorkerIdString } from '../utils/workerAgentIds';
import {
  formatOptimizationDatasetNameForApi,
  normalizeOptimizationDatasetNames,
  parseDefaultsDatasetConfig,
} from '../utils/optimizationDatasetName';
import { hasAnyPersistedOptimizationJobId } from '../utils/optimizationJobLifecycle';
import { mergeConfiguratorModelOptions } from '../utils/configuratorModels';

type WorkerAgentConfig = {
  /** Backend agent id (UUID or legacy numeric string like "1") */
  id: string;
  name: string;
  prompt: string;
  model: string;
  temperature: number;
  description: string;
  mode?: 'synchronous' | 'asynchronous';
  reminder_prompt?: string;
};

type AdditionalWorkerConfig = {
  name: string;
  prompt: string;
  model: string;
  temperature: number;
  description: string;
};

type MultiAgentDefaults = {
  selectedDataset: string;
  multiAgentIterations: number;
  coordinatorPrompt: string;
  coordinatorModel: string;
  coordinatorTemperature: number;
  coordinatorDescription: string;
  coordinatorReminderPrompt: string;
  /** Display name for coordinator (master-agent) in UI */
  coordinatorName: string;
  workers: WorkerAgentConfig[];
  additionalWorkerConfig: AdditionalWorkerConfig;
};

// Интерфейс для сообщений чата
export interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  intermediateMessages?: Array<{
    type: 'ai';
    content: string;
    name: string;
    id: string;
  }>;
}

const cloneWorkers = (workers: WorkerAgentConfig[]): WorkerAgentConfig[] =>
  workers.map((worker) => ({ ...worker }));

const cloneAdditionalWorkerConfig = (config: AdditionalWorkerConfig): AdditionalWorkerConfig => ({
  ...config
});

const areWorkersEqual = (a: WorkerAgentConfig[], b: WorkerAgentConfig[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const workerA = a[i];
    const workerB = b[i];
    if (!workerB) return false;
    if (
      String(workerA.id) !== String(workerB.id) ||
      workerA.name !== workerB.name ||
      workerA.prompt !== workerB.prompt ||
      workerA.model !== workerB.model ||
      workerA.temperature !== workerB.temperature ||
      workerA.description !== workerB.description ||
      (workerA.reminder_prompt || '') !== (workerB.reminder_prompt || '')
    ) {
      return false;
    }
  }
  return true;
};

const areAdditionalWorkerConfigsEqual = (
  a: AdditionalWorkerConfig,
  b: AdditionalWorkerConfig
): boolean =>
  a.name === b.name &&
  a.prompt === b.prompt &&
  a.model === b.model &&
  a.temperature === b.temperature &&
  a.description === b.description;

const DEFAULT_COORDINATOR_DESCRIPTION = 'Coordinator node that communicates with worker nodes to respond to user messages.';

const INITIAL_WORKERS: WorkerAgentConfig[] = [
  {
    id: '1',
    name: 'Expert',
    prompt: 'You are a helpful assistant.',
    model: 'gpt-4.1-mini',
    temperature: 0.70,
    description: 'AI agent that follows this prompt:\nYou are a helpful assistant.'
  },
  {
    id: '2',
    name: 'Synthesizer',
    prompt: 'You are a helpful assistant.',
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    description: 'AI agent that follows this prompt:\nYou are a helpful assistant.'
  }
];

const INITIAL_ADDITIONAL_WORKER_CONFIG: AdditionalWorkerConfig = {
  name: 'Assistant',
  prompt: 'You are a helpful assistant',
  model: 'gpt-4.1-mini',
  temperature: 0.7,
  description: 'General-purpose assistant'
};

interface OptimizationState {
  // Основные данные
  initialPrompt: string;
  botPrompt: string;
  selectedPrompt: string;
  selectedPromptIndex: string;
  optimizedPrompts: string[];
  iterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }>;
  isOptimized: boolean;
  
  // Состояние оптимизации
  isOptimizing: boolean;
  optimizationProgress: number;
  
  // Экспоненциальный прогресс-бар
  progressStartTime: number | null; // Время начала текущей стадии (timestamp)
  currentProgressStage: number; // Текущая стадия (0, 1, 2, ..., iterations-1)
  expectedIterationTime: number; // Ожидаемое время одной итерации в секундах
  
  // Настройки
  selectedDataset: string;
  /** Selected dataset labels for optimization (API: dataset_name string | string[]). */
  selectedDatasets: string[];
  selectedModel: string;
  temperature: number;
  iterations: number;
  multiAgentIterations: number; // Отдельное поле для мультиагентных итераций
  optimizationType: 'standard' | 'with_integration'; // Новое поле для типа оптимизации
  
  // Флаг инициализации для отслеживания первой загрузки дефолтов
  isDefaultsLoaded: boolean;
  
  // Мультиагентные настройки
  workers: WorkerAgentConfig[];
  coordinatorPrompt: string;
  coordinatorModel: string;
  coordinatorTemperature: number;
  coordinatorDescription: string;
  coordinatorReminderPrompt: string;
  /** Display name for coordinator in multi-agent UI (from defaults / API). */
  coordinatorName: string;
  
  // Конфигурация для дополнительных worker агентов (3-й и далее)
  additionalWorkerConfig: AdditionalWorkerConfig;
  
  // Конфигурация для ConnectorPage (отдельная от MultiAgentPage)
  connectorWorkers: WorkerAgentConfig[];
  connectorCoordinatorPrompt: string;
  connectorCoordinatorModel: string;
  connectorCoordinatorTemperature: number;
  connectorCoordinatorDescription: string;
  connectorCoordinatorReminderPrompt: string;
  /** Coordinator display name from Integration Configurator */
  connectorCoordinatorName: string;
  /** Coordinator backend id from integration config (UUID). */
  connectorCoordinatorBackendId?: string;
  /** Coordinator agent_id when distinct from id. */
  connectorCoordinatorAgentId?: string;
  /** Placement of reminder text in connector optimization config (default before). */
  connectorReminderPromptPosition: 'before' | 'after';
  connectorConfigLabel?: string;
  connectorMetaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  };

  multiAgentDefaults: MultiAgentDefaults;
  
  // Мультиагентные результаты оптимизации
  multiAgentOptimizedPrompts: string[];
  multiAgentIterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    "meta_prompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    "metaPrompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }>;
  multiAgentIsOptimized: boolean;
  multiAgentSelectedIterationIndex: number;
  multiAgentSelectedPrompt: string;
  multiAgentSelectedPromptIndex: string;
  multiAgentGraphSliderValue: number;
  multiAgentIsOptimizing: boolean;
  multiAgentOptimizationProgress: number;
  
  // Экспоненциальный прогресс-бар для multi-agent
  multiAgentProgressStartTime: number | null;
  multiAgentCurrentProgressStage: number;
  multiAgentExpectedIterationTime: number;

  // Мультиагентные результаты оптимизации для ConnectorPage (отдельные от MultiAgentPage)
  connectorMultiAgentOptimizedPrompts: string[];
  connectorMultiAgentIterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number;
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    "meta_prompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    "metaPrompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }>;
  connectorMultiAgentIsOptimized: boolean;
  connectorMultiAgentSelectedIterationIndex: number;
  connectorMultiAgentSelectedPrompt: string;
  connectorMultiAgentSelectedPromptIndex: string;
  connectorMultiAgentGraphSliderValue: number;
  connectorMultiAgentIsOptimizing: boolean;
  connectorMultiAgentOptimizationProgress: number;
  
  // Экспоненциальный прогресс-бар для connector multi-agent
  connectorMultiAgentProgressStartTime: number | null;
  connectorMultiAgentCurrentProgressStage: number;
  connectorMultiAgentExpectedIterationTime: number;

  // Чат для Connector Multi-Agent
  connectorMultiAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  connectorMultiAgentChatInputs: {
    before: string;
    after: string;
  };

  optimizedTemperature?: number | null;
  
  // Доступные опции
  availableDatasets: string[];
  availableModels: string[];
  
  // Данные для графиков
  embeddingData: number[];
  iterationData: number[];
  // Качество и токены актора — отдельные абсолютные ряды, не индексируются от iterationData
  currentIteration: number;
  graphSliderValue: number;
  
  // Чат для Single-Agent
  singleAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  singleAgentChatInputs: {
    before: string;
    after: string;
  };
  
  // Чат для Multi-Agent
  multiAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  multiAgentChatInputs: {
    before: string;
    after: string;
  };
  
  // Действия
  setInitialPrompt: (prompt: string) => void;
  setBotPrompt: (prompt: string) => void;
  setSelectedPrompt: (prompt: string) => void;
  setSelectedPromptIndex: (index: string) => void;
  setOptimizedPrompts: (prompts: string[]) => void;
  setIterationResults: (results: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }>) => void;
  setIsOptimized: (isOptimized: boolean) => void;
  setIsOptimizing: (isOptimizing: boolean) => void;
  setOptimizationProgress: (progress: number) => void;
  setProgressStartTime: (time: number | null) => void;
  setCurrentProgressStage: (stage: number) => void;
  setExpectedIterationTime: (time: number) => void;
  setSelectedDataset: (dataset: string) => void;
  setSelectedDatasets: (datasets: string[]) => void;
  setSelectedModel: (model: string) => void;
  setTemperature: (temperature: number) => void;
  setIterations: (iterations: number) => void;
  setMultiAgentIterations: (iterations: number) => void; // Новое действие для мультиагентных итераций
  setOptimizationType: (type: 'standard' | 'with_integration') => void; // Новое действие
  setWorkers: (workers: WorkerAgentConfig[]) => void;
  setCoordinatorPrompt: (prompt: string) => void;
  setCoordinatorModel: (model: string) => void;
  setCoordinatorTemperature: (temperature: number) => void;
  setCoordinatorDescription: (description: string) => void;
  setCoordinatorReminderPrompt: (value: string) => void;
  setCoordinatorName: (name: string) => void;
  
  // Действия для ConnectorPage конфигурации
  setConnectorWorkers: (workers: WorkerAgentConfig[]) => void;
  setConnectorCoordinatorPrompt: (prompt: string) => void;
  setConnectorCoordinatorModel: (model: string) => void;
  setConnectorCoordinatorTemperature: (temperature: number) => void;
  setConnectorCoordinatorDescription: (description: string) => void;
  setConnectorCoordinatorReminderPrompt: (value: string) => void;
  setConnectorCoordinatorName: (name: string) => void;
  setConnectorCoordinatorBackendId: (value?: string) => void;
  setConnectorCoordinatorAgentId: (value?: string) => void;
  setConnectorReminderPromptPosition: (value: 'before' | 'after') => void;
  setConnectorConfigLabel: (label?: string) => void;
  setConnectorMetaPrompts: (metaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  }) => void;
  
  // Мультиагентные действия
  setMultiAgentOptimizedPrompts: (prompts: string[]) => void;
  setMultiAgentIterationResults: (results: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
  }>) => void;
  setMultiAgentIsOptimized: (isOptimized: boolean) => void;
  setMultiAgentSelectedIterationIndex: (index: number) => void;
  setMultiAgentSelectedPrompt: (prompt: string) => void;
  setMultiAgentSelectedPromptIndex: (index: string) => void;
  setMultiAgentGraphSliderValue: (value: number) => void;
  setMultiAgentIsOptimizing: (isOptimizing: boolean) => void;
  setMultiAgentOptimizationProgress: (progress: number) => void;
  setMultiAgentProgressStartTime: (time: number | null) => void;
  setMultiAgentCurrentProgressStage: (stage: number) => void;
  setMultiAgentExpectedIterationTime: (time: number) => void;
  
  // Действия для Connector Multi-Agent
  setConnectorMultiAgentOptimizedPrompts: (prompts: string[]) => void;
  setConnectorMultiAgentIterationResults: (results: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number;
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
  }>) => void;
  setConnectorMultiAgentIsOptimized: (isOptimized: boolean) => void;
  setConnectorMultiAgentSelectedIterationIndex: (index: number) => void;
  setConnectorMultiAgentSelectedPrompt: (prompt: string) => void;
  setConnectorMultiAgentSelectedPromptIndex: (index: string) => void;
  setConnectorMultiAgentGraphSliderValue: (value: number) => void;
  setConnectorMultiAgentIsOptimizing: (isOptimizing: boolean) => void;
  setConnectorMultiAgentOptimizationProgress: (progress: number) => void;
  setConnectorMultiAgentProgressStartTime: (time: number | null) => void;
  setConnectorMultiAgentCurrentProgressStage: (stage: number) => void;
  setConnectorMultiAgentExpectedIterationTime: (time: number) => void;
  addConnectorMultiAgentIterationResult: (result: {
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number;
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    "meta_prompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    "metaPrompts"?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }) => void;
  processConnectorMultiAgentOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  setConnectorMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setConnectorMultiAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setConnectorMultiAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addConnectorMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearConnectorMultiAgentChat: () => void;
  
  addMultiAgentIterationResult: (result: {
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  processMultiAgentOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
    "master-agent"?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    "worker-agents"?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  setAvailableDatasets: (datasets: string[]) => void;
  setAvailableModels: (models: string[]) => void;
  setEmbeddingData: (data: number[]) => void;
  setIterationData: (data: number[]) => void;
  setCurrentIteration: (iteration: number) => void;
  setGraphSliderValue: (value: number) => void;
  addEmbeddingPoint: (value: number) => void;
  addIterationPoint: (value: number) => void;
  
  // Новые функции для работы с данными из бэкенда
  addBackendEmbeddingPoint: (point: { x: number; y: number }) => void;
  addBackendEvaluationPoint: (point: { x: number; y: number }) => void;
  clearGraphData: () => void;
  
  // Функция для обработки результатов оптимизации из бэкенда
  processOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
  }) => void;
  
  // Функция для постепенного добавления промптов
  addOptimizedPrompt: (prompt: string) => void;
  
  // Функция для обработки промежуточных результатов
  processIntermediateResult: (result: {
    prompt?: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration?: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }) => void;

  // Сброс состояния
  resetOptimization: () => void;
  
  // Новые поля
  selectedIterationIndex: number;
  setSelectedIterationIndex: (index: number) => void;
  
  // Функция для загрузки конфигурации по умолчанию
  loadDefaults: () => Promise<void>;
  
  iterationsMax: number;
  
  // Функция для сохранения результатов в историю
  saveToHistory: () => Promise<void>;
  
  // Функция для загрузки конфигурации из истории
  loadFromHistory: (historyItem: {
    initial_prompt: string;
    optimized_prompts: string[];
    dataset_name: string | string[];
    model_name: string;
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name: string;
      temperature: number;
    }>;
  }) => void;
  
  // Функция для сброса настроек к дефолтам
  resetToDefaults: () => Promise<void>;
  
  // Действия для работы с чатами Single-Agent
  setSingleAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setSingleAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setSingleAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addSingleAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearSingleAgentChat: () => void;
  
  // Действия для работы с чатами Multi-Agent
  setMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setMultiAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setMultiAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearMultiAgentChat: () => void;
  isMultiAgentAtDefaults: () => boolean;
}

export const useOptimizationStore = create<OptimizationState>()(
  persist(
    (set, get) => ({
  // Начальные значения
  initialPrompt: '',
  botPrompt: '',
  selectedPrompt: '',
  selectedPromptIndex: '',
  optimizedPrompts: [],
  iterationResults: [],
  isOptimized: false,
  isOptimizing: false,
  optimizationProgress: 0,
  progressStartTime: null,
  currentProgressStage: 0,
  expectedIterationTime: 60, // По умолчанию 60 секунд на итерацию
  selectedDataset: '',
  selectedDatasets: [],
  selectedModel: 'gpt-4o',
  temperature: 0.70,
  iterations: 6,
  multiAgentIterations: 4, // Значение по умолчанию для мультиагентных итераций
  optimizationType: 'standard', // Устанавливаем стандартный тип по умолчанию
  isDefaultsLoaded: false, // Флаг для отслеживания первой загрузки дефолтов
  workers: cloneWorkers(INITIAL_WORKERS),
  coordinatorPrompt: 'You are a helpful assistant.',
  coordinatorModel: 'gpt-4.1-mini',
  coordinatorTemperature: 0.70,
  coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
  coordinatorReminderPrompt: '',
  coordinatorName: 'Coordinator',
  
  // Конфигурация для дополнительных worker агентов по умолчанию
  additionalWorkerConfig: cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG),
  
  // Конфигурация для ConnectorPage по умолчанию
  connectorWorkers: cloneWorkers(INITIAL_WORKERS),
  connectorCoordinatorPrompt: 'You are a helpful assistant.',
  connectorCoordinatorModel: 'gpt-4.1-mini',
  connectorCoordinatorTemperature: 0.70,
  connectorCoordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
  connectorCoordinatorReminderPrompt: '',
  connectorCoordinatorName: 'Coordinator',
  connectorCoordinatorBackendId: undefined,
  connectorCoordinatorAgentId: undefined,
  connectorReminderPromptPosition: 'before',
  connectorConfigLabel: undefined,
  connectorMetaPrompts: undefined,

  multiAgentDefaults: {
    selectedDataset: '',
    multiAgentIterations: 4,
    coordinatorPrompt: 'You are a helpful assistant.',
    coordinatorModel: 'gpt-4.1-mini',
    coordinatorTemperature: 0.70,
    coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
    coordinatorReminderPrompt: '',
    coordinatorName: 'Coordinator',
    workers: cloneWorkers(INITIAL_WORKERS),
    additionalWorkerConfig: cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG)
  },
  
  // Мультиагентные результаты оптимизации
    multiAgentOptimizedPrompts: [],
    multiAgentIterationResults: [],
    multiAgentIsOptimized: false,
    multiAgentSelectedIterationIndex: 0,
    multiAgentSelectedPrompt: '',
    multiAgentSelectedPromptIndex: '',
    multiAgentGraphSliderValue: 0,
    multiAgentIsOptimizing: false,
  multiAgentOptimizationProgress: 0,
  
  // Экспоненциальный прогресс-бар для multi-agent
  multiAgentProgressStartTime: null,
  multiAgentCurrentProgressStage: 0,
  multiAgentExpectedIterationTime: 60,
  
  // Мультиагентные результаты оптимизации для ConnectorPage
  connectorMultiAgentOptimizedPrompts: [],
  connectorMultiAgentIterationResults: [],
  connectorMultiAgentIsOptimized: false,
  connectorMultiAgentSelectedIterationIndex: 0,
  connectorMultiAgentSelectedPrompt: '',
  connectorMultiAgentSelectedPromptIndex: '',
  connectorMultiAgentGraphSliderValue: 0,
  connectorMultiAgentIsOptimizing: false,
  connectorMultiAgentOptimizationProgress: 0,
  connectorMultiAgentProgressStartTime: null,
  connectorMultiAgentCurrentProgressStage: 0,
  connectorMultiAgentExpectedIterationTime: 60,
  
  // Чат для Connector Multi-Agent
  connectorMultiAgentChatMessages: {
    before: [],
    after: []
  },
  connectorMultiAgentChatInputs: {
    before: '',
    after: ''
  },
  
  availableDatasets: [],
  availableModels: mergeConfiguratorModelOptions(['gpt-4.1-mini']),
  embeddingData: [],
  iterationData: [],
  currentIteration: 0,
  graphSliderValue: 0,
  selectedIterationIndex: 0,
  iterationsMax: 10,
  optimizedTemperature: null,
  
  // Начальные значения для чатов Single-Agent
  singleAgentChatMessages: {
    before: [],
    after: []
  },
  singleAgentChatInputs: {
    before: '',
    after: ''
  },
  
  // Начальные значения для чатов Multi-Agent
  multiAgentChatMessages: {
    before: [],
    after: []
  },
  multiAgentChatInputs: {
    before: '',
    after: ''
  },
  
  // Действия
  setInitialPrompt: (prompt: string) => set({ initialPrompt: prompt }),
  setBotPrompt: (prompt: string) => set({ botPrompt: prompt }),
  setSelectedPrompt: (prompt: string) => set({ selectedPrompt: prompt }),
  setSelectedPromptIndex: (index: string) => set({ selectedPromptIndex: index }),
  setOptimizedPrompts: (prompts: string[]) => set({ optimizedPrompts: prompts }),
  setIterationResults: (results) => set({ iterationResults: results }),
  setIsOptimized: (isOptimized: boolean) => {
    console.log('🔧 setIsOptimized called with:', isOptimized);
    set({ isOptimized });
  },
  setIsOptimizing: (isOptimizing: boolean) => set({ isOptimizing }),
  setOptimizationProgress: (progress: number) => set((state) => {
    const incoming = Math.max(0, Math.min(100, progress));
    const current = typeof state.optimizationProgress === 'number' ? state.optimizationProgress : 0;
    
    // ✅ Разрешаем обнуление прогресса (для повторного запуска оптимизации)
    // Если incoming === 0, значит это начало новой оптимизации - принудительно обнуляем
    const next = incoming === 0 ? 0 : Math.max(current, incoming);
    
    console.log('📊 setOptimizationProgress:', { current, incoming, next });
    
    if (next !== current) {
      return { optimizationProgress: next };
    }
    return {};
  }),
  setProgressStartTime: (time: number | null) => set({ progressStartTime: time }),
  setCurrentProgressStage: (stage: number) => set({ currentProgressStage: stage }),
  setExpectedIterationTime: (time: number) => set({ expectedIterationTime: time }),
  setSelectedDataset: (dataset: string) => {
    const trimmed = (dataset || '').trim();
    set({
      selectedDataset: trimmed,
      selectedDatasets: trimmed ? [trimmed] : [],
    });
  },
  setSelectedDatasets: (datasets: string[]) => {
    const normalized = normalizeOptimizationDatasetNames(datasets);
    const current = get().selectedDatasets;
    if (
      normalized.length === current.length &&
      normalized.every((d, i) => d === current[i])
    ) {
      return;
    }
    set({
      selectedDatasets: normalized,
      selectedDataset: normalized[0] || '',
    });
  },
  setSelectedModel: (model: string) => set({ selectedModel: model }),
  setTemperature: (temperature: number) => {
    const numTemp = Number(temperature);
    console.log('🔄 setTemperature called with:', temperature, 'converted to:', numTemp);
    set({ temperature: numTemp });
  },
  setIterations: (iterations: number) => {
    const numIterations = Number(iterations);
    const currentState = get();
    const maxIterations = currentState.iterationsMax || 10;
    
    // Убеждаемся что значение в допустимых пределах
    const validIterations = Math.max(1, Math.min(numIterations, maxIterations));
    
    set({ iterations: validIterations });
  },
  setMultiAgentIterations: (iterations: number) => {
    const numIterations = Number(iterations);
    const currentState = get();
    const maxIterations = currentState.iterationsMax || 10;
    
    // Убеждаемся что значение в допустимых пределах
    const validIterations = Math.max(1, Math.min(numIterations, maxIterations));
    
    set({ multiAgentIterations: validIterations });
  },
  setOptimizationType: (type: 'standard' | 'with_integration') => set({ optimizationType: type }),
  setWorkers: (workers: WorkerAgentConfig[]) =>
    set({
      workers: cloneWorkers(
        workers.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) }))
      ),
    }),
  setCoordinatorPrompt: (prompt: string) => set({ coordinatorPrompt: prompt }),
  setCoordinatorModel: (model: string) => set({ coordinatorModel: model }),
  setCoordinatorTemperature: (temperature: number) => set({ coordinatorTemperature: Math.min(temperature, 1) }),
  setCoordinatorDescription: (description: string) => set({ coordinatorDescription: description }),
  setCoordinatorReminderPrompt: (value: string) => set({ coordinatorReminderPrompt: value }),
  setCoordinatorName: (name: string) => set({ coordinatorName: name.trim() || 'Coordinator' }),
  
  // Действия для ConnectorPage конфигурации
  setConnectorWorkers: (workers: WorkerAgentConfig[]) =>
    set({
      connectorWorkers: cloneWorkers(
        workers.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) }))
      ),
    }),
  setConnectorCoordinatorPrompt: (prompt: string) => set({ connectorCoordinatorPrompt: prompt }),
  setConnectorCoordinatorModel: (model: string) => set({ connectorCoordinatorModel: model }),
  setConnectorCoordinatorTemperature: (temperature: number) => set({ connectorCoordinatorTemperature: Math.min(temperature, 1) }),
  setConnectorCoordinatorDescription: (description: string) => set({ connectorCoordinatorDescription: description }),
  setConnectorCoordinatorReminderPrompt: (value: string) => set({ connectorCoordinatorReminderPrompt: value }),
  setConnectorCoordinatorName: (name: string) => set({ connectorCoordinatorName: name.trim() || 'Coordinator' }),
  setConnectorCoordinatorBackendId: (value?: string) =>
    set({ connectorCoordinatorBackendId: value?.trim() || undefined }),
  setConnectorCoordinatorAgentId: (value?: string) =>
    set({ connectorCoordinatorAgentId: value?.trim() || undefined }),
  setConnectorReminderPromptPosition: (value: 'before' | 'after') =>
    set({ connectorReminderPromptPosition: value }),
  setConnectorConfigLabel: (label?: string) => set({ connectorConfigLabel: label }),
  setConnectorMetaPrompts: (metaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  }) => set({ connectorMetaPrompts: metaPrompts }),

  isMultiAgentAtDefaults: () => {
    const state = get();
    const defaults = state.multiAgentDefaults;

    if (!defaults) {
      return true;
    }

    const datasetMatches = (state.selectedDataset || '') === (defaults.selectedDataset || '');
    const iterationsMatch = state.multiAgentIterations === defaults.multiAgentIterations;
    const coordinatorMatches =
      state.coordinatorPrompt === defaults.coordinatorPrompt &&
      state.coordinatorModel === defaults.coordinatorModel &&
      state.coordinatorTemperature === defaults.coordinatorTemperature &&
      state.coordinatorDescription === defaults.coordinatorDescription &&
      (state.coordinatorReminderPrompt || '') === (defaults.coordinatorReminderPrompt || '') &&
      (state.coordinatorName || 'Coordinator') === (defaults.coordinatorName || 'Coordinator');
    const additionalMatches = areAdditionalWorkerConfigsEqual(
      state.additionalWorkerConfig,
      defaults.additionalWorkerConfig
    );
    const workersMatch = areWorkersEqual(state.workers, defaults.workers);

    return datasetMatches && iterationsMatch && coordinatorMatches && additionalMatches && workersMatch;
  },
  
  // Мультиагентные действия
  setMultiAgentOptimizedPrompts: (prompts: string[]) => set({ multiAgentOptimizedPrompts: prompts }),
  setMultiAgentIterationResults: (results) => set({ multiAgentIterationResults: results }),
  setMultiAgentIsOptimized: (isOptimized: boolean) => set({ multiAgentIsOptimized: isOptimized }),
  setMultiAgentSelectedIterationIndex: (index: number) => {
    console.log(`🔄 setMultiAgentSelectedIterationIndex called with: ${index}`);
    set({ multiAgentSelectedIterationIndex: index });
  },
  setMultiAgentSelectedPrompt: (prompt: string) => set({ multiAgentSelectedPrompt: prompt }),
  setMultiAgentSelectedPromptIndex: (index: string) => set({ multiAgentSelectedPromptIndex: index }),
  setMultiAgentGraphSliderValue: (value: number) => set({ multiAgentGraphSliderValue: value }),
  setMultiAgentIsOptimizing: (isOptimizing: boolean) => set({ multiAgentIsOptimizing: isOptimizing }),
  setMultiAgentOptimizationProgress: (progress: number) => set((state: OptimizationState) => {
    const incoming = Math.max(0, Math.min(100, progress));
    const current = typeof state.multiAgentOptimizationProgress === 'number' ? state.multiAgentOptimizationProgress : 0;
    
    // ✅ Разрешаем обнуление прогресса (для повторного запуска оптимизации)
    // Если incoming === 0, значит это начало новой оптимизации - принудительно обнуляем
    const next = incoming === 0 ? 0 : Math.max(current, incoming);
    
    console.log('📊 setMultiAgentOptimizationProgress:', { current, incoming, next });
    
    if (next !== current) {
      return { multiAgentOptimizationProgress: next };
    }
    return {};
  }),
  setMultiAgentProgressStartTime: (time: number | null) => set({ multiAgentProgressStartTime: time }),
  setMultiAgentCurrentProgressStage: (stage: number) => set({ multiAgentCurrentProgressStage: stage }),
  setMultiAgentExpectedIterationTime: (time: number) => set({ multiAgentExpectedIterationTime: time }),
  addMultiAgentIterationResult: (result) => set((state: OptimizationState) => {
    console.log('➕ addMultiAgentIterationResult called with:', result);
    
    const seq = typeof result.seq === 'number' ? result.seq : undefined;
    const incomingSeqOrIter = seq !== undefined ? seq : result.iteration;

    // Deduplicate by seq/iteration: if this step already exists, do not append again
    if (typeof incomingSeqOrIter === 'number') {
      const alreadyExists = state.multiAgentIterationResults.some((r) => {
        const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
        return existingSeqOrIter === incomingSeqOrIter;
      });
      if (alreadyExists) {
        console.log(`♻️ Skipping duplicate multi-agent result for seq=${incomingSeqOrIter}`);
        return {};
      }
    }
    
    // seq: 0 - это baseline evaluation, добавляем только в iterationResults, но НЕ в optimizedPrompts
    if (seq === 0) {
      console.log('📊 Multi-agent seq: 0 - baseline evaluation, adding to iterationResults only (not to optimizedPrompts)');
      
      // Создаем маппинг для замены имен агентов на кастомные имена
      const agentNameMapping: { [key: string]: string } = {};
      agentNameMapping['master'] = 'Coordinator';
      
      if (result["worker-agents"] && state.workers) {
        result["worker-agents"].forEach((apiAgent: any, index: number) => {
          const customAgent = state.workers[index];
          if (customAgent && apiAgent.name) {
            agentNameMapping[apiAgent.name] = customAgent.name;
            agentNameMapping[`worker${index + 1}`] = customAgent.name;
            agentNameMapping[`worker-${index + 1}`] = customAgent.name;
          }
        });
      }
      
      const updatedResult = {
        ...result,
        seq: 0,
        iteration: 0,
        "master-agent": result["master-agent"] ? {
          ...result["master-agent"],
          name: agentNameMapping[result["master-agent"].name] || result["master-agent"].name
        } : undefined,
        // Устанавливаем mode: 'synchronous' для всех worker-agents до получения seq 0
        "worker-agents": result["worker-agents"]?.map((agent: any) => ({
          ...agent,
          name: agentNameMapping[agent.name] || agent.name,
          mode: 'synchronous' // Все агенты в synchronous до получения seq 0
        }))
      };
      
      return {
        multiAgentIterationResults: [...state.multiAgentIterationResults, updatedResult]
      };
    }
    
    // seq: 1, 2, 3... - это оптимизированные конфигурации
    // Создаем маппинг для замены имен агентов на кастомные имена
    const agentNameMapping: { [key: string]: string } = {};
    
    // Маппинг для master-agent
    agentNameMapping['master'] = 'Coordinator';
    
    // Маппинг для worker агентов
    if (result["worker-agents"] && state.workers) {
      result["worker-agents"].forEach((apiAgent: any, index: number) => {
        const customAgent = state.workers[index];
        if (customAgent && apiAgent.name) {
          agentNameMapping[apiAgent.name] = customAgent.name;
          agentNameMapping[`worker${index + 1}`] = customAgent.name;
          agentNameMapping[`worker-${index + 1}`] = customAgent.name;
        }
      });
    }
    
    // Обновляем результат с кастомными именами
    const updatedResult = {
      ...result,
      seq: seq !== undefined ? seq : result.iteration,
      iteration: seq !== undefined ? seq : result.iteration,
      "master-agent": result["master-agent"] ? {
        ...result["master-agent"],
        name: agentNameMapping[result["master-agent"].name] || result["master-agent"].name
      } : undefined,
      "worker-agents": result["worker-agents"]?.map((agent: any) => ({
        ...agent,
        name: agentNameMapping[agent.name] || agent.name
      }))
    };
    
    console.log('🔄 Updated result with custom names:', updatedResult);
    
    // Добавляем промпт в optimizedPrompts только для seq > 0
    const masterPrompt = updatedResult["master-agent"]?.prompt || updatedResult.prompt;
    const currentOptimizedCount = state.multiAgentOptimizedPrompts.length;
    const maxIterations = state.multiAgentIterations || 4;
    
    // Проверяем лимит оптимизированных промптов
    if (currentOptimizedCount < maxIterations && masterPrompt) {
      const updatedPrompts = [...state.multiAgentOptimizedPrompts, masterPrompt];
      
      const newResults = [...state.multiAgentIterationResults, updatedResult];
      console.log('📊 Updated multiAgentIterationResults:', newResults);
      return {
        multiAgentIterationResults: newResults,
        multiAgentOptimizedPrompts: updatedPrompts
      };
    }
    
    const newResults = [...state.multiAgentIterationResults, updatedResult];
    console.log('📊 Updated multiAgentIterationResults:', newResults);
    return {
      multiAgentIterationResults: newResults
    };
  }),
  processMultiAgentOptimizationResult: (result) => set((state: OptimizationState) => {
    console.log('🔄 processMultiAgentOptimizationResult called with:', result);
    
    // Фильтруем промпты: исключаем seq: 0 (baseline evaluation)
    let updatedPrompts = state.multiAgentOptimizedPrompts;
    if (result.prompts && result.prompts.length > 0) {
      // Фильтруем промпты, исключая seq: 0
      updatedPrompts = result.prompts;
      console.log('📝 Updated prompts from result.prompts:', updatedPrompts);
    } else if (result.prompt) {
      // Проверяем, что это не seq: 0 (baseline evaluation)
      const lastResult = state.multiAgentIterationResults[state.multiAgentIterationResults.length - 1];
      if (lastResult && lastResult.seq !== 0) {
        updatedPrompts = [...state.multiAgentOptimizedPrompts, result.prompt];
        console.log('📝 Added single prompt to existing prompts:', updatedPrompts);
      } else {
        console.log('📝 Skipping prompt addition - seq: 0 (baseline evaluation)');
      }
    }

    // НЕ добавляем новый результат в iterationResults, так как промежуточные результаты уже добавлены
    // Просто обновляем промпты и флаг оптимизации
    return {
      multiAgentOptimizedPrompts: updatedPrompts,
      multiAgentIsOptimized: true
    };
  }),
  setAvailableDatasets: (datasets: string[]) => set({ availableDatasets: datasets }),
  setAvailableModels: (models: string[]) => set({ availableModels: models }),
  setEmbeddingData: (data: number[]) => set({ embeddingData: data }),
  setIterationData: (data: number[]) => set({ iterationData: data }),
  setCurrentIteration: (iteration: number) => set({ currentIteration: iteration }),
  setGraphSliderValue: (value: number) => set({ graphSliderValue: value }),
  addEmbeddingPoint: (value: number) => set((state: any) => ({ 
    embeddingData: [...state.embeddingData, value] 
  })),
  addIterationPoint: (value: number) => set((state: any) => ({ 
    iterationData: [...state.iterationData, value] 
  })),
  setSelectedIterationIndex: (index: number) => set({ selectedIterationIndex: index }),
  
  // Действия для работы с чатами Single-Agent
  setSingleAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => 
    set({ singleAgentChatMessages: messages }),
  setSingleAgentChatInputs: (inputs: { before: string; after: string }) => 
    set({ singleAgentChatInputs: inputs }),
  setSingleAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      singleAgentChatInputs: {
        ...state.singleAgentChatInputs,
        [type]: value
      }
    })),
  addSingleAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => 
    set((state) => ({
      singleAgentChatMessages: {
        ...state.singleAgentChatMessages,
        [type]: [...state.singleAgentChatMessages[type], message]
      }
    })),
  clearSingleAgentChat: () => set({
    singleAgentChatMessages: { before: [], after: [] },
    singleAgentChatInputs: { before: '', after: '' }
  }),
  
  // Действия для работы с чатами Multi-Agent
  setMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => 
    set({ multiAgentChatMessages: messages }),
  setMultiAgentChatInputs: (inputs: { before: string; after: string }) => 
    set({ multiAgentChatInputs: inputs }),
  setMultiAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      multiAgentChatInputs: {
        ...state.multiAgentChatInputs,
        [type]: value
      }
    })),
  addMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => 
    set((state) => ({
      multiAgentChatMessages: {
        ...state.multiAgentChatMessages,
        [type]: [...state.multiAgentChatMessages[type], message]
      }
    })),
  clearMultiAgentChat: () => set({
    multiAgentChatMessages: { before: [], after: [] },
    multiAgentChatInputs: { before: '', after: '' }
  }),
  
  // Действия для Connector Multi-Agent
  setConnectorMultiAgentOptimizedPrompts: (prompts: string[]) => set({ connectorMultiAgentOptimizedPrompts: prompts }),
  setConnectorMultiAgentIterationResults: (results) => set({ connectorMultiAgentIterationResults: results }),
  setConnectorMultiAgentIsOptimized: (isOptimized: boolean) => set({ connectorMultiAgentIsOptimized: isOptimized }),
  setConnectorMultiAgentSelectedIterationIndex: (index: number) => set({ connectorMultiAgentSelectedIterationIndex: index }),
  setConnectorMultiAgentSelectedPrompt: (prompt: string) => set({ connectorMultiAgentSelectedPrompt: prompt }),
  setConnectorMultiAgentSelectedPromptIndex: (index: string) => set({ connectorMultiAgentSelectedPromptIndex: index }),
  setConnectorMultiAgentGraphSliderValue: (value: number) => set({ connectorMultiAgentGraphSliderValue: value }),
  setConnectorMultiAgentIsOptimizing: (isOptimizing: boolean) => set({ connectorMultiAgentIsOptimizing: isOptimizing }),
  setConnectorMultiAgentOptimizationProgress: (progress: number) => set((state: OptimizationState) => {
    const incoming = Math.max(0, Math.min(100, progress));
    const current = typeof state.connectorMultiAgentOptimizationProgress === 'number' ? state.connectorMultiAgentOptimizationProgress : 0;
    const next = incoming === 0 ? 0 : Math.max(current, incoming);
    if (next !== current) {
      return { connectorMultiAgentOptimizationProgress: next };
    }
    return {};
  }),
  setConnectorMultiAgentProgressStartTime: (time: number | null) => set({ connectorMultiAgentProgressStartTime: time }),
  setConnectorMultiAgentCurrentProgressStage: (stage: number) => set({ connectorMultiAgentCurrentProgressStage: stage }),
  setConnectorMultiAgentExpectedIterationTime: (time: number) => set({ connectorMultiAgentExpectedIterationTime: time }),
  addConnectorMultiAgentIterationResult: (result) => set((state: OptimizationState) => {
    const seq = typeof result.seq === 'number' ? result.seq : undefined;
    const incomingSeqOrIter = seq !== undefined ? seq : result.iteration;

    // Deduplicate by seq/iteration
    if (typeof incomingSeqOrIter === 'number') {
      const alreadyExists = state.connectorMultiAgentIterationResults.some((r) => {
        const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
        return existingSeqOrIter === incomingSeqOrIter;
      });
      if (alreadyExists) {
        return {};
      }
    }
    
    // seq: 0 - baseline evaluation
    if (seq === 0) {
      return {
        connectorMultiAgentIterationResults: [...state.connectorMultiAgentIterationResults, {
          ...result,
          seq: 0,
          iteration: 0
        }]
      };
    }
    
    // seq: 1, 2, 3... - optimized configurations
    const updatedResult = {
      ...result,
      seq: seq !== undefined ? seq : result.iteration,
      iteration: seq !== undefined ? seq : result.iteration
    };
    
    const masterPrompt = updatedResult["master-agent"]?.prompt || updatedResult.prompt;
    const currentOptimizedCount = state.connectorMultiAgentOptimizedPrompts.length;
    const maxIterations = state.multiAgentIterations || 4;
    
    if (currentOptimizedCount < maxIterations && masterPrompt) {
      return {
        connectorMultiAgentIterationResults: [...state.connectorMultiAgentIterationResults, updatedResult],
        connectorMultiAgentOptimizedPrompts: [...state.connectorMultiAgentOptimizedPrompts, masterPrompt]
      };
    }
    
    return {
      connectorMultiAgentIterationResults: [...state.connectorMultiAgentIterationResults, updatedResult]
    };
  }),
  processConnectorMultiAgentOptimizationResult: (result) => set((state: OptimizationState) => {
    let updatedPrompts = state.connectorMultiAgentOptimizedPrompts;
    if (result.prompts && result.prompts.length > 0) {
      updatedPrompts = result.prompts;
    } else if (result.prompt) {
      const lastResult = state.connectorMultiAgentIterationResults[state.connectorMultiAgentIterationResults.length - 1];
      if (lastResult && lastResult.seq !== 0) {
        updatedPrompts = [...state.connectorMultiAgentOptimizedPrompts, result.prompt];
      }
    }

    return {
      connectorMultiAgentOptimizedPrompts: updatedPrompts,
    };
  }),
  setConnectorMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => 
    set({ connectorMultiAgentChatMessages: messages }),
  setConnectorMultiAgentChatInputs: (inputs: { before: string; after: string }) => 
    set({ connectorMultiAgentChatInputs: inputs }),
  setConnectorMultiAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      connectorMultiAgentChatInputs: {
        ...state.connectorMultiAgentChatInputs,
        [type]: value
      }
    })),
  addConnectorMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => 
    set((state) => ({
      connectorMultiAgentChatMessages: {
        ...state.connectorMultiAgentChatMessages,
        [type]: [...state.connectorMultiAgentChatMessages[type], message]
      }
    })),
  clearConnectorMultiAgentChat: () => set({
    connectorMultiAgentChatMessages: { before: [], after: [] },
    connectorMultiAgentChatInputs: { before: '', after: '' }
  }),
  
  // Новые функции для работы с данными из бэкенда
  addBackendEmbeddingPoint: (point: { x: number; y: number }) => set((state: any) => ({ 
    embeddingData: [...state.embeddingData, point.x, point.y] 
  })),
  addBackendEvaluationPoint: (point: { x: number; y: number }) => set((state: any) => ({ 
    iterationData: [...state.iterationData, point.x, point.y] 
  })),
  clearGraphData: () => set({
    embeddingData: [],
    iterationData: [],
    iterationResults: [],
    optimizedPrompts: [],
    currentIteration: 0,
    graphSliderValue: 0,
  }),
  
  // Функция для обработки результатов оптимизации из бэкенда
  processOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
  }) => set((state: any) => ({
    iterationResults: [...state.iterationResults, {
      prompt: result.prompt || '',
      embedding_point: result.embedding_point,
      evaluation_point: result.evaluation_point,
      iteration: state.currentIteration + 1,
      temperature: typeof result.temperature === 'number' ? result.temperature : state.optimizedTemperature ?? state.temperature
    }],
    currentIteration: state.currentIteration + 1,
    optimizedTemperature: typeof result.temperature === 'number' ? result.temperature : state.optimizedTemperature
  })),
  
  // Функция для постепенного добавления промптов
  addOptimizedPrompt: (prompt: string) => set((state: any) => {
    // Если это первый промпт или массив пустой, заменяем массив
    // Иначе добавляем к существующим
    const updatedPrompts = state.optimizedPrompts.length === 0 
      ? [prompt] 
      : [...state.optimizedPrompts, prompt];
    
    return {
      optimizedPrompts: updatedPrompts,
      isOptimized: true
    };
  }),
  
  // Функция для обработки промежуточных результатов
  processIntermediateResult: (result: {
    prompt?: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration?: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }) => set((state: any) => {
    const newPrompt = result.prompt || '';
    const incomingTemp = typeof result.temperature === 'number' ? result.temperature : undefined;
    const seq = typeof result.seq === 'number' ? result.seq : undefined;


    console.log('🔄 processIntermediateResult:', {
      newPrompt,
      seq,
      currentPrompts: state.optimizedPrompts,
      currentIteration: state.currentIteration,
      iterations: state.iterations,
      iterationResultsLength: state.iterationResults.length
    });
    
    // seq: 0 - это оценочный ответ (baseline evaluation)
    // Показываем только в графике как точка 0 и в истории как step 0
    // НЕ добавляем в optimizedPrompts
    if (seq === 0) {
      console.log('📊 seq: 0 - baseline evaluation, adding to iterationResults only (not to optimizedPrompts)');
      
      // Добавляем точки на график для baseline evaluation (точка 0)
      let updatedEmbeddingData = [...state.embeddingData];
      let updatedIterationData = [...state.iterationData];
      
      if (result.embedding_point) {
        console.log('📈 Adding baseline embedding point (seq: 0) to graph:', result.embedding_point);
        // Проверяем, не добавляем ли мы дублирующую точку
        const existingPoint = state.embeddingData.find((_: number, index: number) => 
          index % 2 === 0 && state.embeddingData[index] === result.embedding_point!.x && 
          state.embeddingData[index + 1] === result.embedding_point!.y
        );
        if (!existingPoint) {
          // Если это первая точка, заменяем массив, иначе добавляем
          updatedEmbeddingData = state.embeddingData.length === 0 
            ? [result.embedding_point.x, result.embedding_point.y]
            : [...state.embeddingData, result.embedding_point.x, result.embedding_point.y];
        }
      }
      
      if (result.evaluation_point) {
        console.log('📈 Adding baseline evaluation point (seq: 0) to graph:', result.evaluation_point);
        // Проверяем, не добавляем ли мы дублирующую точку
        const existingPoint = state.iterationData.find((_: number, index: number) => 
          index % 2 === 0 && state.iterationData[index] === result.evaluation_point!.x && 
          state.iterationData[index + 1] === result.evaluation_point!.y
        );
        if (!existingPoint) {
          // Если это первая точка, заменяем массив, иначе добавляем
          updatedIterationData = state.iterationData.length === 0 
            ? [result.evaluation_point.x, result.evaluation_point.y]
            : [...state.iterationData, result.evaluation_point.x, result.evaluation_point.y];
        }
      }
      

      // Deduplicate baseline by seq/iteration 0
      const hasBaseline = state.iterationResults.some((r: any) => r.seq === 0 || r.iteration === 0);
      if (hasBaseline) {
        console.log('♻️ Skipping duplicate baseline (seq=0) for single-agent');
        return {
          embeddingData: updatedEmbeddingData,
          iterationData: updatedIterationData,
          currentIteration: state.currentIteration,
          optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature
        };
      }

      return {
        iterationResults: [...state.iterationResults, {
          prompt: newPrompt,
          embedding_point: result.embedding_point,
          evaluation_point: result.evaluation_point,
          quality_point: result.quality_point,
          actor_tokens_point: result.actor_tokens_point,
          iteration: 0, // seq: 0 соответствует iteration: 0
          seq: 0,
          temperature: incomingTemp ?? state.temperature
        }],
        embeddingData: updatedEmbeddingData,
        iterationData: updatedIterationData,
        currentIteration: state.currentIteration,
        optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature
      };
    }
    
    // seq: 1, 2, 3... - это оптимизированные конфигурации
    // Добавляем в optimizedPrompts и в iterationResults
    const currentOptimizedCount = state.optimizedPrompts.length;
    const maxIterations = state.iterations || 4;
    
    // Проверяем лимит промптов. Если превышен — не добавляем в optimizedPrompts,
    // но ВСЕ РАВНО добавляем точку и iterationResults (важно для restore и графиков).
    const willAppendPrompt = currentOptimizedCount < maxIterations;
    
    // Правильно нумеруем итерацию для оптимизированных промптов
    // seq: 1 -> iteration: 1, seq: 2 -> iteration: 2, etc.
    const optimizedIterationNumber = seq !== undefined ? seq : (currentOptimizedCount + 1);
    
    // Deduplicate by seq/iteration: skip if already have this step
    const alreadyExists = state.iterationResults.some((r: any) => {
      const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
      return existingSeqOrIter === optimizedIterationNumber;
    });
    
    const updatedPrompts = (!alreadyExists && willAppendPrompt)
      ? [...state.optimizedPrompts, newPrompt]
      : state.optimizedPrompts;
    
    // Добавляем точки на график для оптимизированных промптов
    let updatedEmbeddingData = [...state.embeddingData];
    let updatedIterationData = [...state.iterationData];
    
    if (result.embedding_point) {
      console.log(`📈 Adding optimized embedding point (seq: ${seq}) to graph:`, result.embedding_point);
      // Проверяем, не добавляем ли мы дублирующую точку
      const existingPoint = state.embeddingData.find((_: number, index: number) => 
        index % 2 === 0 && state.embeddingData[index] === result.embedding_point!.x && 
        state.embeddingData[index + 1] === result.embedding_point!.y
      );
      if (!existingPoint) {
        updatedEmbeddingData = [...state.embeddingData, result.embedding_point.x, result.embedding_point.y];
      }
    }
    
    if (result.evaluation_point) {
      console.log(`📈 Adding optimized evaluation point (seq: ${seq}) to graph:`, result.evaluation_point);
      // Проверяем, не добавляем ли мы дублирующую точку
      const existingPoint = state.iterationData.find((_: number, index: number) => 
        index % 2 === 0 && state.iterationData[index] === result.evaluation_point!.x && 
        state.iterationData[index + 1] === result.evaluation_point!.y
      );
      if (!existingPoint) {
        updatedIterationData = [...state.iterationData, result.evaluation_point.x, result.evaluation_point.y];
      }
    }


    console.log(`📝 Updated prompts (optimized): ${updatedPrompts.length}/${maxIterations} (willAppend=${willAppendPrompt})`);
    console.log(`📈 Total embedding points: ${updatedEmbeddingData.length / 2}, Total evaluation points: ${updatedIterationData.length / 2}`);

    // If this iteration already exists, only update graph data; don't append duplicates
    if (alreadyExists) {
      console.log(`♻️ Skipping duplicate single-agent result for seq=${optimizedIterationNumber}`);
      return {
        optimizedPrompts: updatedPrompts,
        embeddingData: updatedEmbeddingData,
        iterationData: updatedIterationData,
        currentIteration: state.currentIteration,
        isOptimized: true,
        optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature
      };
    }
    
    return {
      optimizedPrompts: updatedPrompts,
      iterationResults: [...state.iterationResults, {
        prompt: newPrompt,
        embedding_point: result.embedding_point,
        evaluation_point: result.evaluation_point,
        quality_point: result.quality_point,
        actor_tokens_point: result.actor_tokens_point,
        iteration: optimizedIterationNumber,
        seq: seq,
        temperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature
      }],
      embeddingData: updatedEmbeddingData,
      iterationData: updatedIterationData,
      currentIteration: optimizedIterationNumber,
      isOptimized: true,
      optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature
    };
  }),
  
  resetOptimization: () => set({
    initialPrompt: '',
    botPrompt: '',
    selectedPrompt: '',
    selectedPromptIndex: '',
    optimizedPrompts: [],
    iterationResults: [],
    isOptimized: false,
    isOptimizing: false,
    optimizationProgress: 0,
    progressStartTime: null,
    currentProgressStage: 0,
    embeddingData: [],
    iterationData: [],
    currentIteration: 0,
    graphSliderValue: 0,
    selectedIterationIndex: 0,
  }),
  
  // Функция для загрузки конфигурации по умолчанию
  loadDefaults: async () => {
    try {
      const { autoTune } = await import('../services/api');
      const defaults = await autoTune.getDefaults();

      console.log('🌐 Store: Raw API response:', defaults);
      console.log('🌐 Store: API data structure:', JSON.stringify(defaults.data, null, 2));

      // Новая структура ответа с endpoint
      const modelOptions = mergeConfiguratorModelOptions(defaults.data?.models?.options || ['gpt-4.1-mini']);
      const defaultModel = defaults.data?.models?.default || 'gpt-4.1-mini';
      const { options: datasetOptions, defaultName: defaultDataset } = parseDefaultsDatasetConfig(
        defaults.data?.datasets
      );
      const defaultSelectedDatasets = defaultDataset ? [defaultDataset] : [];
      const defaultTemperature = defaults.data?.temperature?.default;
      const safeTemperature = isNaN(Number(defaultTemperature)) ? 0.7 : Number(defaultTemperature);
      const defaultIterations = defaults.data?.iterations?.default;
      const maxIterations = defaults.data?.iterations?.max || 10;
      const safeIterations = isNaN(Number(defaultIterations)) ? 4 : Math.max(1, Math.min(Number(defaultIterations), maxIterations));
      const defaultPrompt = defaults.data?.default_prompt || 'You are a helpful assistant.';

      // Обработка мульти-агентных дефолтов
      const masterAgent = defaults.data?.['master-agent'];
      const workerAgents = defaults.data?.['worker-agents'];
      const additionalWorker = defaults.data?.['additional-worker'];
      
      console.log('🤖 Store: Extracted master-agent:', masterAgent);
      console.log('🤖 Store: Extracted worker-agents:', workerAgents);
      console.log('🤖 Store: Extracted additional-worker:', additionalWorker);
      
      let coordinatorPrompt = defaultPrompt;
      let coordinatorModel = defaultModel;
      let coordinatorTemperature = safeTemperature;
      let coordinatorDescription = DEFAULT_COORDINATOR_DESCRIPTION;
      let coordinatorReminderPrompt = '';
      let coordinatorName = 'Coordinator';
      
      // Конфигурация для дополнительных worker агентов
      let additionalWorkerConfig = {
        name: 'Assistant',
        prompt: 'You are a helpful assistant',
        model: defaultModel,
        temperature: safeTemperature,
        description: 'General-purpose assistant'
      };
      
      let workers: WorkerAgentConfig[] = [
        {
          id: '1',
          name: 'Expert',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`
        },
        {
          id: '2',
          name: 'Synthesizer',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`
        }
      ];

      if (masterAgent || workerAgents) {
        console.log('🤖 Store: Processing multi-agent defaults:', { masterAgent, workerAgents });
        
        // Обрабатываем master agent
        if (masterAgent) {
          coordinatorPrompt = masterAgent.prompt || defaultPrompt;
          coordinatorModel = masterAgent.model_name || defaultModel;
          coordinatorTemperature = masterAgent.temperature || safeTemperature;
          coordinatorDescription = masterAgent.description || `Coordinator node that communicates with worker nodes to respond to user messages.`;
          coordinatorReminderPrompt =
            typeof masterAgent.reminder_prompt === 'string' ? masterAgent.reminder_prompt : '';
          coordinatorName =
            typeof masterAgent.name === 'string' && masterAgent.name.trim()
              ? masterAgent.name.trim()
              : 'Coordinator';
        }

        // Обрабатываем worker agents
        if (workerAgents && workerAgents.length > 0) {
          workers = workerAgents.map((worker: any, index: number) => ({
            id: normalizeWorkerIdString(worker?.id, index),
            name: worker.name || `Worker ${index + 1}`,
            prompt: worker.prompt || defaultPrompt,
            model: worker.model_name || defaultModel,
            temperature: worker.temperature || safeTemperature,
            description: worker.description || `AI agent that follows this prompt:\n${worker.prompt || defaultPrompt}`,
            reminder_prompt:
              typeof worker.reminder_prompt === 'string' ? worker.reminder_prompt : undefined,
          }));
        }
      }
      
      // Обрабатываем конфигурацию additional-worker
      if (additionalWorker) {
        console.log('🤖 Store: Processing additional-worker config:', additionalWorker);
        additionalWorkerConfig = {
          name: additionalWorker.name || 'Assistant',
          prompt: additionalWorker.prompt || 'You are a helpful assistant',
          model: additionalWorker.model_name || defaultModel,
          temperature: additionalWorker.temperature || safeTemperature,
          description: additionalWorker.description || 'General-purpose assistant'
        };
      }

      console.log('🔧 Store: Parsed values:', {
        modelOptions,
        defaultModel,
        datasetOptions,
        defaultDataset,
        safeTemperature,
        safeIterations,
        defaultPrompt,
        masterAgent: masterAgent ? 'present' : 'not present',
        workerAgents: workerAgents ? `${workerAgents.length} agents` : 'not present',
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        workersCount: workers.length
      });

      const defaultsSnapshot: MultiAgentDefaults = {
        selectedDataset: defaultDataset || '',
        multiAgentIterations: safeIterations,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig)
      };

      const currentState = get();
      
      // Проверяем, есть ли уже сохраненные пользовательские настройки
      // Если дефолты еще не загружались, всегда загружаем их
      // Если уже загружались, проверяем наличие пользовательских изменений
      const hasUserSettings = currentState.isDefaultsLoaded && (
        currentState.selectedModel !== '' || 
        currentState.coordinatorPrompt !== '' || 
        currentState.workers.length > 0
      );

      const resolveSelectedDatasets = (): string[] => {
        if (!hasUserSettings) return defaultSelectedDatasets;
        const fromList = normalizeOptimizationDatasetNames(currentState.selectedDatasets).filter((d) =>
          datasetOptions.includes(d)
        );
        if (fromList.length > 0) return fromList;
        const legacy = currentState.selectedDataset?.trim();
        if (legacy && datasetOptions.includes(legacy)) return [legacy];
        return defaultSelectedDatasets;
      };
      const nextSelectedDatasets = resolveSelectedDatasets();
      
      // Обновляем только доступные опции и дефолты, если нет пользовательских настроек
      set({
        // Всегда обновляем доступные опции
        availableModels: modelOptions,
        availableDatasets: datasetOptions,
        iterationsMax: maxIterations,
        
        // Обновляем только если нет пользовательских настроек
        selectedModel: hasUserSettings ? currentState.selectedModel : defaultModel,
        temperature: hasUserSettings ? currentState.temperature : safeTemperature,
        iterations: hasUserSettings ? currentState.iterations : safeIterations,
        multiAgentIterations: hasUserSettings ? currentState.multiAgentIterations : safeIterations,
        selectedDataset: hasUserSettings
          ? nextSelectedDatasets[0] || currentState.selectedDataset
          : defaultDataset || '',
        selectedDatasets: nextSelectedDatasets,
        
        // Промпты - используем сохраненные или дефолтные
        initialPrompt: currentState.initialPrompt || defaultPrompt,
        botPrompt: currentState.botPrompt || defaultPrompt,
        
        // Мультиагентные настройки - используем сохраненные или дефолтные
        coordinatorPrompt: hasUserSettings ? currentState.coordinatorPrompt : coordinatorPrompt,
        coordinatorModel: hasUserSettings ? currentState.coordinatorModel : coordinatorModel,
        coordinatorTemperature: hasUserSettings ? currentState.coordinatorTemperature : coordinatorTemperature,
        coordinatorDescription: hasUserSettings ? currentState.coordinatorDescription : coordinatorDescription,
        coordinatorReminderPrompt: hasUserSettings ? currentState.coordinatorReminderPrompt : coordinatorReminderPrompt,
        coordinatorName: hasUserSettings ? currentState.coordinatorName : coordinatorName,
        workers: hasUserSettings ? currentState.workers : cloneWorkers(workers),
        additionalWorkerConfig: hasUserSettings ? currentState.additionalWorkerConfig : cloneAdditionalWorkerConfig(additionalWorkerConfig),
        multiAgentDefaults: defaultsSnapshot,
        
        // Устанавливаем флаг, что дефолты загружены
        isDefaultsLoaded: true
      });
      
      console.log('🔄 loadDefaults set iterations to:', hasUserSettings ? currentState.iterations : safeIterations);
      console.log('🔄 loadDefaults set temperature to:', hasUserSettings ? currentState.temperature : safeTemperature);
      console.log('🔄 loadDefaults set default prompt to:', defaultPrompt);
      console.log('🔄 loadDefaults set coordinatorPrompt to:', coordinatorPrompt);
      console.log('🔄 loadDefaults set coordinatorModel to:', coordinatorModel);
      console.log('🔄 loadDefaults set coordinatorTemperature to:', coordinatorTemperature);
      console.log('🔄 loadDefaults set coordinatorDescription to:', coordinatorDescription);
      console.log('🔄 loadDefaults set workers to:', workers);

      console.log('✅ Loaded defaults from backend:', defaults);
      
      // Проверяем финальное состояние store
      const finalState = get();
      console.log('🎯 Store: Final state after loadDefaults:', {
        coordinatorPrompt: finalState.coordinatorPrompt,
        coordinatorModel: finalState.coordinatorModel,
        coordinatorTemperature: finalState.coordinatorTemperature,
        workers: finalState.workers,
        availableModels: finalState.availableModels,
        availableDatasets: finalState.availableDatasets
      });
    } catch (error) {
      console.error('❌ Failed to load defaults:', error);
      // Используем значения по умолчанию при ошибке
      const fallbackWorkers = cloneWorkers(INITIAL_WORKERS);
      const fallbackAdditionalWorker = cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG);

      set({
        selectedModel: 'gpt-4.1-mini',
        temperature: 0.7,
        iterations: 6,
        multiAgentIterations: 4,
        availableModels: mergeConfiguratorModelOptions(['gpt-4.1-mini']),
        availableDatasets: [],
        selectedDataset: '',
        iterationsMax: 10,
        initialPrompt: 'You are a helpful assistant.',
        botPrompt: 'You are a helpful assistant.',
        // Мультиагентные fallback значения
        coordinatorPrompt: 'You are a helpful assistant.',
        coordinatorModel: 'gpt-4.1-mini',
        coordinatorTemperature: 0.7,
        coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
        coordinatorReminderPrompt: '',
        coordinatorName: 'Coordinator',
        workers: fallbackWorkers,
        additionalWorkerConfig: fallbackAdditionalWorker,
        multiAgentDefaults: {
          selectedDataset: '',
          multiAgentIterations: 4,
          coordinatorPrompt: 'You are a helpful assistant.',
          coordinatorModel: 'gpt-4.1-mini',
          coordinatorTemperature: 0.7,
          coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
          coordinatorReminderPrompt: '',
          coordinatorName: 'Coordinator',
          workers: cloneWorkers(fallbackWorkers),
          additionalWorkerConfig: cloneAdditionalWorkerConfig(fallbackAdditionalWorker)
        }
      });
    }
  },
  
  // Функция для сохранения результатов в историю
  saveToHistory: async () => {
    try {
      // const { autoTune } = await import('../services/api'); // Временно отключено
      const state = get();
      
      console.log('💾 saveToHistory called with state:', {
        optimizedPromptsLength: state.optimizedPrompts.length,
        optimizedPrompts: state.optimizedPrompts,
        botPrompt: state.botPrompt,
        selectedDataset: state.selectedDataset,
        selectedModel: state.selectedModel,
        temperature: state.optimizedTemperature ?? state.temperature,
        iterations: state.iterations,
        iterationResultsLength: state.iterationResults.length
      });
      
      if (state.optimizedPrompts.length > 0 && state.botPrompt) {
        // Создаем массив steps для истории
        const steps: Array<{
          step_number: number;
          prompt: string;
          embedding_point?: { x: number; y: number };
          evaluation_point?: { x: number; y: number };
          timestamp: string;
          model_name: string;
          temperature: number;
        }> = [];
        
        // Step 0: baseline evaluation с данными конфигурации
        const baselineResult = state.iterationResults.find(r => r.seq === 0 || r.iteration === 0);
        const step0Prompt = baselineResult?.prompt || state.botPrompt;
        const step0Temperature = baselineResult?.temperature ?? state.temperature;
        const step0Model = state.selectedModel;
        
        steps.push({
          step_number: 0,
          prompt: step0Prompt,
          embedding_point: baselineResult?.embedding_point,
          evaluation_point: baselineResult?.evaluation_point,
          timestamp: new Date().toISOString(),
          model_name: step0Model,
          temperature: step0Temperature
        });
        
        // Добавляем остальные steps из iterationResults (seq: 1, 2, 3...)
        state.iterationResults
          .filter(r => r.seq !== 0 && r.seq !== undefined && r.seq > 0) // Только оптимизированные (seq: 1+)
          .sort((a, b) => (a.seq ?? a.iteration) - (b.seq ?? b.iteration)) // Сортируем по seq
          .forEach((result) => {
            const seq = result.seq ?? result.iteration;
            steps.push({
              step_number: seq,
              prompt: result.prompt,
              embedding_point: result.embedding_point,
              evaluation_point: result.evaluation_point,
              timestamp: new Date().toISOString(),
              model_name: step0Model, // Используем модель из конфигурации
              temperature: result.temperature ?? step0Temperature
            });
          });
        
        const saveData = {
          run_id: `run_${Date.now()}`, // Generate a unique run ID
          initial_prompt: state.botPrompt,
          optimized_prompts: state.optimizedPrompts,
          dataset_name:
            formatOptimizationDatasetNameForApi(
              state.selectedDatasets.length > 0
                ? state.selectedDatasets
                : state.selectedDataset
                  ? [state.selectedDataset]
                  : []
            ) ?? '',
          model_name: state.selectedModel,
          temperature: state.optimizedTemperature ?? state.temperature,
          iterations: state.iterations,
          steps: steps // Добавляем массив steps с step 0
        };
        
        console.log('📤 Sending to saveToHistory API:', saveData);
        console.log('📋 Steps array:', steps);
        
        // Временно отключаем сохранение в историю, так как эндпоинт может не существовать
        // const response = await autoTune.saveToHistory(saveData);
        // console.log('✅ Optimization results saved to history:', response);
        console.log('⚠️ saveToHistory temporarily disabled - endpoint may not exist');
      } else {
        console.warn('⚠️ Cannot save to history: no optimized prompts or bot prompt');
      }
    } catch (error) {
      console.error('❌ Failed to save optimization results to history:', error);
      // Не выбрасываем ошибку, чтобы не прерывать работу приложения
      // throw error;
    }
  },
  
  // Функция для загрузки конфигурации из истории
  loadFromHistory: (historyItem: {
    initial_prompt: string;
    optimized_prompts: string[];
    dataset_name: string | string[];
    model_name: string;
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt?: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name?: string;
      temperature?: number;
      data?: {
        "master-agent"?: {
          name: string;
          prompt: string;
          model_name: string;
          temperature: number;
          description?: string;
          reminder_prompt?: string;
        };
        "worker-agents"?: Array<{
          name: string;
          prompt: string;
          model_name: string;
          temperature: number;
          description?: string;
          mode?: 'synchronous' | 'asynchronous';
          reminder_prompt?: string;
        }>;
      };
    }>;
  }) => {
    console.log('🔄 loadFromHistory called with:', historyItem);
    
    // Создаем iterationResults с точками графиков
    let iterationResults: Array<{
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      iteration: number;
      seq?: number;
      temperature?: number;
    }> = [];
    
    // Добавляем все шаги оптимизации, включая первый
    if (historyItem.steps && historyItem.steps.length > 0) {
      // Проверяем, есть ли step 0 и заполнен ли он
      const step0 = historyItem.steps.find(s => s.step_number === 0);
      
      // Функция для извлечения данных из шага (из data или прямых полей)
      const extractStepData = (step: any) => {
        // Если есть данные в step.data, используем их
        const stepDataMaster = step.data?.["master-agent"];
        if (stepDataMaster && (!step.prompt || !step.model_name)) {
          return {
            prompt: step.prompt || stepDataMaster.prompt || '',
            model_name: step.model_name || stepDataMaster.model_name || '',
            temperature: step.temperature !== undefined ? step.temperature : (stepDataMaster.temperature ?? 0)
          };
        }
        // Иначе используем прямые поля
        return {
          prompt: step.prompt || '',
          model_name: step.model_name || '',
          temperature: step.temperature ?? 0
        };
      };
      
      // Если step 0 отсутствует или пустой, создаем его из конфигурации
      const isEmptyStep0 = !step0 || (!step0.prompt && !step0.model_name && !step0.data?.["master-agent"]);
      
      if (isEmptyStep0) {
        // Создаем step 0 с данными конфигурации
        const step0Data: {
          prompt: string;
          embedding_point?: { x: number; y: number };
          evaluation_point?: { x: number; y: number };
          iteration: number;
          seq: number;
          temperature: number;
        } = {
          prompt: historyItem.initial_prompt,
          iteration: 0,
          seq: 0,
          temperature: historyItem.temperature
        };
        
        // Если есть step 0 с данными точек, сохраняем их
        if (step0) {
          step0Data.embedding_point = step0.embedding_point;
          step0Data.evaluation_point = step0.evaluation_point;
        }
        
        iterationResults.push(step0Data);
      } else {
        // Step 0 существует, извлекаем данные
        const step0Extracted = extractStepData(step0);
        iterationResults.push({
          prompt: step0Extracted.prompt,
          embedding_point: step0.embedding_point,
          evaluation_point: step0.evaluation_point,
          iteration: 0,
          seq: 0,
          temperature: step0Extracted.temperature
        });
      }
      
      // Добавляем остальные шаги оптимизации (step_number > 0)
      historyItem.steps
        .filter(step => step.step_number > 0)
        .sort((a, b) => a.step_number - b.step_number)
        .forEach((step) => {
          const stepExtracted = extractStepData(step);
          iterationResults.push({
            prompt: stepExtracted.prompt,
            embedding_point: step.embedding_point,
            evaluation_point: step.evaluation_point,
            iteration: step.step_number,
            seq: step.step_number,
            temperature: stepExtracted.temperature
          });
        });
    } else {
      // Fallback: если нет steps, создаем базовые iterationResults
      iterationResults = historyItem.optimized_prompts.map((prompt, index) => ({
        prompt: prompt,
        iteration: index + 1
      }));
    }
    
    // Создаем данные для графиков из iterationResults
    const embeddingData: number[] = [];
    const iterationData: number[] = [];
    
    iterationResults.forEach(result => {
      if (result.embedding_point) {
        embeddingData.push(result.embedding_point.x, result.embedding_point.y);
      }
      if (result.evaluation_point) {
        iterationData.push(result.evaluation_point.x, result.evaluation_point.y);
      }
    });
    
    const historyDatasets = normalizeOptimizationDatasetNames(historyItem.dataset_name);
    set({
      initialPrompt: historyItem.initial_prompt, // Устанавливаем initialPrompt для отображения в Initial Configuration
      botPrompt: historyItem.initial_prompt, // Устанавливаем botPrompt тоже
      optimizedPrompts: historyItem.optimized_prompts,
      selectedDatasets: historyDatasets,
      selectedDataset: historyDatasets[0] || '',
      selectedModel: historyItem.model_name,
      temperature: historyItem.temperature,
      iterations: historyItem.iterations, // Используем количество итераций из истории
      iterationsMax: 10, // Оставляем стандартный максимум
      isOptimized: true,
      selectedIterationIndex: 0,
      currentIteration: historyItem.iterations, // Используем количество итераций из истории
      iterationResults: iterationResults,
      embeddingData: embeddingData,
      iterationData: iterationData
    });
    
    console.log('✅ loadFromHistory completed:', {
      iterations: historyItem.iterations,
      iterationResultsCount: iterationResults.length,
      embeddingDataPoints: embeddingData.length / 2,
      iterationDataPoints: iterationData.length / 2
    });
  },
  
  // Функция для сброса настроек к дефолтам
  resetToDefaults: async () => {
    try {
      const { autoTune } = await import('../services/api');
      const defaults = await autoTune.getDefaults();

      console.log('🔄 Resetting to defaults from API:', defaults);

      // Извлекаем дефолтные значения
      const modelOptions = mergeConfiguratorModelOptions(defaults.data?.models?.options || ['gpt-4.1-mini']);
      const defaultModel = defaults.data?.models?.default || 'gpt-4.1-mini';
      const { options: datasetOptions, defaultName: defaultDataset } = parseDefaultsDatasetConfig(
        defaults.data?.datasets
      );
      const defaultSelectedDatasets = defaultDataset ? [defaultDataset] : [];
      const defaultTemperature = defaults.data?.temperature?.default;
      const safeTemperature = isNaN(Number(defaultTemperature)) ? 0.7 : Number(defaultTemperature);
      const defaultIterations = defaults.data?.iterations?.default;
      const maxIterations = defaults.data?.iterations?.max || 10;
      const safeIterations = isNaN(Number(defaultIterations)) ? 4 : Math.max(1, Math.min(Number(defaultIterations), maxIterations));
      const defaultPrompt = defaults.data?.default_prompt || 'You are a helpful assistant.';

      // Обработка мульти-агентных дефолтов
      const masterAgent = defaults.data?.['master-agent'];
      const workerAgents = defaults.data?.['worker-agents'];
      const additionalWorker = defaults.data?.['additional-worker'];
      
      let coordinatorPrompt = defaultPrompt;
      let coordinatorModel = defaultModel;
      let coordinatorTemperature = safeTemperature;
      let coordinatorDescription = DEFAULT_COORDINATOR_DESCRIPTION;
      let coordinatorReminderPrompt = '';
      let coordinatorName = 'Coordinator';
      
      let additionalWorkerConfig = {
        name: 'Assistant',
        prompt: 'You are a helpful assistant',
        model: defaultModel,
        temperature: safeTemperature,
        description: 'General-purpose assistant'
      };
      
      let workers: WorkerAgentConfig[] = [
        {
          id: '1',
          name: 'Expert',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`
        },
        {
          id: '2',
          name: 'Synthesizer',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`
        }
      ];

      if (masterAgent || workerAgents) {
        if (masterAgent) {
          coordinatorPrompt = masterAgent.prompt || defaultPrompt;
          coordinatorModel = masterAgent.model_name || defaultModel;
          coordinatorTemperature = masterAgent.temperature || safeTemperature;
          coordinatorDescription = masterAgent.description || `Coordinator node that communicates with worker nodes to respond to user messages.`;
          coordinatorReminderPrompt =
            typeof masterAgent.reminder_prompt === 'string' ? masterAgent.reminder_prompt : '';
          coordinatorName =
            typeof masterAgent.name === 'string' && masterAgent.name.trim()
              ? masterAgent.name.trim()
              : 'Coordinator';
        }

        if (workerAgents && workerAgents.length > 0) {
          workers = workerAgents.map((worker: any, index: number) => ({
            id: normalizeWorkerIdString(worker?.id, index),
            name: worker.name || `Worker ${index + 1}`,
            prompt: worker.prompt || defaultPrompt,
            model: worker.model_name || defaultModel,
            temperature: worker.temperature || safeTemperature,
            description: worker.description || `AI agent that follows this prompt:\n${worker.prompt || defaultPrompt}`,
            reminder_prompt:
              typeof worker.reminder_prompt === 'string' ? worker.reminder_prompt : undefined,
          }));
        }
      }
      
      if (additionalWorker) {
        additionalWorkerConfig = {
          name: additionalWorker.name || 'Assistant',
          prompt: additionalWorker.prompt || 'You are a helpful assistant',
          model: additionalWorker.model_name || defaultModel,
          temperature: additionalWorker.temperature || safeTemperature,
          description: additionalWorker.description || 'General-purpose assistant'
        };
      }

      // Принудительно сбрасываем все настройки к дефолтам
      const defaultsSnapshot: MultiAgentDefaults = {
        selectedDataset: defaultDataset || '',
        multiAgentIterations: safeIterations,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig)
      };

      set({
        selectedModel: defaultModel,
        temperature: safeTemperature,
        iterations: safeIterations,
        multiAgentIterations: safeIterations,
        availableModels: modelOptions,
        availableDatasets: datasetOptions,
        selectedDataset: defaultDataset || '',
        selectedDatasets: defaultSelectedDatasets,
        iterationsMax: maxIterations,
        initialPrompt: defaultPrompt,
        botPrompt: defaultPrompt,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig),
        multiAgentDefaults: defaultsSnapshot
      });

      console.log('✅ Settings reset to defaults successfully');
    } catch (error) {
      console.error('❌ Failed to reset to defaults:', error);
    }
  },
}),
    {
      name: 'optimization-store',
      merge: (persisted, current) => {
        const p = persisted as Partial<OptimizationState> | undefined;
        const base = { ...current, ...(p ?? {}) };
        const mapIds = (list: WorkerAgentConfig[] | undefined, fallback: WorkerAgentConfig[]) =>
          Array.isArray(list)
            ? list.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) }))
            : fallback;
        return {
          ...base,
          availableModels: mergeConfiguratorModelOptions(
            Array.isArray(p?.availableModels) ? p!.availableModels : current.availableModels
          ),
          connectorReminderPromptPosition:
            p?.connectorReminderPromptPosition === 'after' ? 'after' : 'before',
          workers: mapIds(base.workers, current.workers),
          connectorWorkers: mapIds(base.connectorWorkers, current.connectorWorkers),
          multiAgentDefaults: base.multiAgentDefaults
            ? {
                ...base.multiAgentDefaults,
                coordinatorName:
                  base.multiAgentDefaults.coordinatorName ??
                  current.multiAgentDefaults?.coordinatorName ??
                  'Coordinator',
                workers: mapIds(
                  base.multiAgentDefaults.workers,
                  current.multiAgentDefaults.workers
                ),
              }
            : current.multiAgentDefaults,
        };
      },
      // Исключаем из сохранения временные состояния оптимизации
      partialize: (state: OptimizationState) => ({
        // Основные настройки пользователя
        selectedDataset: state.selectedDataset,
        selectedDatasets: state.selectedDatasets,
        selectedModel: state.selectedModel,
        temperature: state.temperature,
        iterations: state.iterations,
        multiAgentIterations: state.multiAgentIterations,
        
        // Экспоненциальный прогресс (сохраняем для восстановления при обновлении страницы)
        progressStartTime: state.progressStartTime,
        currentProgressStage: state.currentProgressStage,
        expectedIterationTime: state.expectedIterationTime,
        isOptimizing: state.isOptimizing,
        // ⚠️ НЕ сохраняем optimizationProgress - он пересчитывается в onRehydrateStorage!
        // Это предотвращает проблему с "застрявшим" 100% при повторном запуске оптимизации
        
        // Экспоненциальный прогресс для multi-agent
        multiAgentProgressStartTime: state.multiAgentProgressStartTime,
        multiAgentCurrentProgressStage: state.multiAgentCurrentProgressStage,
        multiAgentExpectedIterationTime: state.multiAgentExpectedIterationTime,
        multiAgentIsOptimizing: state.multiAgentIsOptimizing,
        // ⚠️ НЕ сохраняем multiAgentOptimizationProgress - он пересчитывается в onRehydrateStorage!
        
        // Мультиагентные настройки
        workers: state.workers,
        coordinatorPrompt: state.coordinatorPrompt,
        coordinatorModel: state.coordinatorModel,
        coordinatorTemperature: state.coordinatorTemperature,
        coordinatorDescription: state.coordinatorDescription,
        coordinatorReminderPrompt: state.coordinatorReminderPrompt,
        coordinatorName: state.coordinatorName,
        additionalWorkerConfig: state.additionalWorkerConfig,
        multiAgentDefaults: state.multiAgentDefaults,
        
        // Конфигурация для ConnectorPage
        connectorWorkers: state.connectorWorkers,
        connectorCoordinatorPrompt: state.connectorCoordinatorPrompt,
        connectorCoordinatorModel: state.connectorCoordinatorModel,
        connectorCoordinatorTemperature: state.connectorCoordinatorTemperature,
        connectorCoordinatorDescription: state.connectorCoordinatorDescription,
        connectorCoordinatorReminderPrompt: state.connectorCoordinatorReminderPrompt,
        connectorCoordinatorName: state.connectorCoordinatorName,
        connectorCoordinatorBackendId: state.connectorCoordinatorBackendId,
        connectorCoordinatorAgentId: state.connectorCoordinatorAgentId,
        connectorReminderPromptPosition: state.connectorReminderPromptPosition,
        connectorConfigLabel: state.connectorConfigLabel,
        
        // Доступные опции
        availableDatasets: state.availableDatasets,
        availableModels: state.availableModels,
        iterationsMax: state.iterationsMax,
        
        // Промпты пользователя
        initialPrompt: state.initialPrompt,
        botPrompt: state.botPrompt,
        
        // Результаты оптимизации для single-agent - критично для работы чата After Optimization
        iterationResults: state.iterationResults,
        isOptimized: state.isOptimized,
        optimizedPrompts: state.optimizedPrompts,
        selectedIterationIndex: state.selectedIterationIndex,
        
        // Флаг инициализации
        isDefaultsLoaded: state.isDefaultsLoaded,
        
        // Чаты - сохраняем между сессиями и переключениями страниц
        singleAgentChatMessages: state.singleAgentChatMessages,
        singleAgentChatInputs: state.singleAgentChatInputs,
        multiAgentChatMessages: state.multiAgentChatMessages,
        multiAgentChatInputs: state.multiAgentChatInputs,
        
        // Мультиагентные результаты оптимизации - критично для работы чата After Optimization
        multiAgentIterationResults: state.multiAgentIterationResults,
        multiAgentIsOptimized: state.multiAgentIsOptimized,
        multiAgentSelectedIterationIndex: state.multiAgentSelectedIterationIndex,
        multiAgentSelectedPrompt: state.multiAgentSelectedPrompt,
        multiAgentSelectedPromptIndex: state.multiAgentSelectedPromptIndex,
        multiAgentGraphSliderValue: state.multiAgentGraphSliderValue,
        multiAgentOptimizedPrompts: state.multiAgentOptimizedPrompts,
        
        // Экспоненциальный прогресс для connector multi-agent
        connectorMultiAgentProgressStartTime: state.connectorMultiAgentProgressStartTime,
        connectorMultiAgentCurrentProgressStage: state.connectorMultiAgentCurrentProgressStage,
        connectorMultiAgentExpectedIterationTime: state.connectorMultiAgentExpectedIterationTime,
        connectorMultiAgentIsOptimizing: state.connectorMultiAgentIsOptimizing,
        // ⚠️ НЕ сохраняем connectorMultiAgentOptimizationProgress - он пересчитывается в onRehydrateStorage!
        
        // Connector evaluation results — session-only (avoid stale Initial/Optimized on page open).
        // Loaded explicitly via optimization run, job polling, or Profile → Load to Connector.
        
        // Чат для Connector Multi-Agent
        connectorMultiAgentChatMessages: state.connectorMultiAgentChatMessages,
        connectorMultiAgentChatInputs: state.connectorMultiAgentChatInputs,
      } as Partial<OptimizationState>),
      // Пересчитываем прогресс при восстановлении из localStorage
      onRehydrateStorage: () => (state) => {
        if (state) {
          if (!state.selectedDatasets?.length && state.selectedDataset?.trim()) {
            state.selectedDatasets = [state.selectedDataset.trim()];
          } else if (state.selectedDatasets?.length) {
            state.selectedDatasets = normalizeOptimizationDatasetNames(state.selectedDatasets);
            state.selectedDataset = state.selectedDatasets[0] || state.selectedDataset || '';
          }

          // 🧹 Очищаем старые значения прогресса из localStorage, если они там остались
          const storedData = localStorage.getItem('optimization-storage');
          if (storedData) {
            try {
              const parsed = JSON.parse(storedData);
              if (parsed.state && ('optimizationProgress' in parsed.state || 'multiAgentOptimizationProgress' in parsed.state)) {
                // Удаляем старые ключи прогресса
                delete parsed.state.optimizationProgress;
                delete parsed.state.multiAgentOptimizationProgress;
                localStorage.setItem('optimization-storage', JSON.stringify(parsed));
                console.log('🧹 Cleaned up legacy progress values from localStorage');
              }
            } catch (e) {
              console.warn('Failed to clean up localStorage:', e);
            }
          }
          
          // Single-agent: пересчитываем прогресс на основе времени
          if (state.isOptimizing && state.progressStartTime !== null) {
            const { 
              calculateExponentialProgress, 
              calculateTotalProgress, 
              getElapsedSeconds 
            } = require('../utils/exponentialProgress');
            
            const elapsedSeconds = getElapsedSeconds(state.progressStartTime);
            const stageProgress = calculateExponentialProgress(elapsedSeconds, state.expectedIterationTime);
            const totalProgress = calculateTotalProgress(
              state.currentProgressStage, 
              state.iterations, 
              stageProgress
            );
            
            // ✅ КРИТИЧНО: Используем setTimeout для вызова сеттера ПОСЛЕ rehydration
            // Прямое изменение state не триггерит обновление подписчиков!
            setTimeout(() => {
              state.setOptimizationProgress?.(totalProgress);
              console.log('🔄 Store rehydrated: Single-agent progress updated via setter:', totalProgress);
            }, 0);
            
            console.log('🔄 Store rehydrated: Single-agent progress recalculated:', {
              elapsedSeconds,
              stageProgress,
              totalProgress,
              currentProgressStage: state.currentProgressStage,
              iterations: state.iterations
            });
          } else {
            // Если оптимизация не активна - обнуляем прогресс
            setTimeout(() => {
              state.setOptimizationProgress?.(0);
              console.log('🔄 Store rehydrated: Single-agent progress reset to 0 via setter');
            }, 0);
            console.log('🔄 Store rehydrated: Single-agent optimization not active');
          }
          
          // Multi-agent: пересчитываем прогресс на основе времени
          // ⚠️ ВАЖНО: Для multi-agent currentProgressStage считает только seq > 0 (оптимизированные)
          if (state.multiAgentIsOptimizing && state.multiAgentProgressStartTime !== null) {
            const { 
              calculateExponentialProgress, 
              calculateTotalProgress, 
              getElapsedSeconds 
            } = require('../utils/exponentialProgress');
            
            const elapsedSeconds = getElapsedSeconds(state.multiAgentProgressStartTime);
            const stageProgress = calculateExponentialProgress(elapsedSeconds, state.multiAgentExpectedIterationTime);
            const totalProgress = calculateTotalProgress(
              state.multiAgentCurrentProgressStage, 
              state.multiAgentIterations, 
              stageProgress
            );
            
            // ✅ КРИТИЧНО: Используем setTimeout для вызова сеттера ПОСЛЕ rehydration
            setTimeout(() => {
              state.setMultiAgentOptimizationProgress?.(totalProgress);
              console.log('🔄 Store rehydrated: Multi-agent progress updated via setter:', totalProgress);
            }, 0);
            
            console.log('🔄 Store rehydrated: Multi-agent progress recalculated:', {
              elapsedSeconds,
              stageProgress,
              totalProgress,
              multiAgentCurrentProgressStage: state.multiAgentCurrentProgressStage,
              multiAgentIterations: state.multiAgentIterations,
              note: 'currentProgressStage counts only seq > 0 (optimized results)'
            });
          } else {
            // Если multi-agent оптимизация не активна - обнуляем прогресс
            setTimeout(() => {
              state.setMultiAgentOptimizationProgress?.(0);
              console.log('🔄 Store rehydrated: Multi-agent progress reset to 0 via setter');
            }, 0);
            console.log('🔄 Store rehydrated: Multi-agent optimization not active');
          }
          
          // 🧹 Очищаем чат "After Optimization", если оптимизация не завершена
          // Single-agent: очищаем чат после, если оптимизация не завершена
          if (!state.isOptimized && (state.singleAgentChatMessages?.after?.length > 0 || state.singleAgentChatInputs?.after)) {
            setTimeout(() => {
              state.setSingleAgentChatMessages?.({
                ...state.singleAgentChatMessages,
                after: []
              });
              state.setSingleAgentChatInput?.('after', '');
              console.log('🧹 Store rehydrated: Single-agent After Optimization chat cleared (optimization not completed)');
            }, 0);
          }
          
          // Multi-agent: очищаем чат после, если оптимизация не завершена
          if (!state.multiAgentIsOptimized && (state.multiAgentChatMessages?.after?.length > 0 || state.multiAgentChatInputs?.after)) {
            setTimeout(() => {
              state.setMultiAgentChatMessages?.({
                ...state.multiAgentChatMessages,
                after: []
              });
              state.setMultiAgentChatInput?.('after', '');
              console.log('🧹 Store rehydrated: Multi-agent After Optimization chat cleared (optimization not completed)');
            }, 0);
          }
          
          // Connector Multi-agent: Jobs API polling owns progress when a job id is persisted.
          if (hasAnyPersistedOptimizationJobId()) {
            setTimeout(() => {
              state.setConnectorMultiAgentProgressStartTime?.(null);
              state.setConnectorMultiAgentOptimizationProgress?.(0);
            }, 0);
          } else if (state.connectorMultiAgentIsOptimizing) {
            setTimeout(() => {
              state.setConnectorMultiAgentIsOptimizing?.(false);
              state.setConnectorMultiAgentProgressStartTime?.(null);
              state.setConnectorMultiAgentCurrentProgressStage?.(0);
              state.setConnectorMultiAgentOptimizationProgress?.(0);
            }, 0);
            console.log('🔄 Store rehydrated: Cleared stale connector optimization run (no active Jobs API id)');
          } else {
            setTimeout(() => {
              state.setConnectorMultiAgentOptimizationProgress?.(0);
              console.log('🔄 Store rehydrated: Connector Multi-agent progress reset to 0 via setter');
            }, 0);
            console.log('🔄 Store rehydrated: Connector Multi-agent optimization not active');
          }
          
          // Connector Multi-agent: очищаем чат после, если оптимизация не завершена
          if (!state.connectorMultiAgentIsOptimized && (state.connectorMultiAgentChatMessages?.after?.length > 0 || state.connectorMultiAgentChatInputs?.after)) {
            setTimeout(() => {
              state.setConnectorMultiAgentChatMessages?.({
                ...state.connectorMultiAgentChatMessages,
                after: []
              });
              state.setConnectorMultiAgentChatInput?.('after', '');
              console.log('🧹 Store rehydrated: Connector Multi-agent After Optimization chat cleared (optimization not completed)');
            }, 0);
          }

          // Connector evaluation: drop stale iteration results unless a run is actively in progress
          if (
            !state.connectorMultiAgentIsOptimizing &&
            state.connectorMultiAgentIterationResults?.length > 0
          ) {
            setTimeout(() => {
              state.setConnectorMultiAgentIterationResults?.([]);
              state.setConnectorMultiAgentOptimizedPrompts?.([]);
              state.setConnectorMultiAgentIsOptimized?.(false);
              state.setConnectorMultiAgentSelectedIterationIndex?.(0);
              console.log('🧹 Store rehydrated: Connector evaluation results cleared (no active run)');
            }, 0);
          }
        }
      },
    }
  )
); 